import BackButton from '../../components/Button/IconButton/BackButton';

import { useCallback, useEffect, useState } from 'react';
import {
    CONTACTS,
    Contact,
    CONVERSATION_STATE_MACHINES,
    ResponseOption,
    TextMessage,
} from './phone_texts';

import './phone-game.css';
import PhoneOutline from '../../assets/images/phone_game/phone.png';
import Contacts from './Contacts';
import Conversation from './Conversation';
import TableBackground from '../../components/Background/TableBackground';
import { MinigameState } from '../minigame_state_defs';
import DialogueBox from '../../components/DialogueBox/DialogueBox';
import { useSoundEffect } from '../../hooks/useSoundEffect';
import MessageSentAudio from '../../assets/audio/message_sent.ogg';
import MessageReceivedAudio from '../../assets/audio/message_received.mp3';

import SAD_TROMBONE_SOUND from '../../assets/audio/sad_trombone.wav';
import SCORE_SOUND from '../../assets/audio/success.mp3';

interface PhoneProps {
    goToLandingPage: () => void;
    markGameDoneCb: () => void;
}

export default function PhoneGame(props: PhoneProps) {
    const { goToLandingPage, markGameDoneCb } = props;

    const [minigameState, setMinigameState] = useState<MinigameState>(
        MinigameState.INTRO
    );
    const [selectedContact, setSelectedContact] = useState<Contact | undefined>(
        undefined
    );

    const [conversationStates, setConversationStates] = useState<
        Record<string, number>
    >(
        CONTACTS.reduce(
            (acc, contact) => {
                acc[contact] = 0;
                return acc;
            },
            {} as Record<string, number>
        )
    );

    const [conversations, setConversations] = useState<
        Record<string, Array<TextMessage>>
    >(
        CONTACTS.reduce(
            (acc, contact) => {
                const firstMessage: TextMessage = {
                    type: 'received',
                    message: CONVERSATION_STATE_MACHINES[contact][0].message,
                };

                acc[contact] = [firstMessage];
                return acc;
            },
            {} as Record<string, Array<TextMessage>>
        )
    );

    const [waitingForResponse, setWaitingForResponse] = useState(false);

    const messageSent = useSoundEffect(MessageSentAudio);
    const messageReceived = useSoundEffect(MessageReceivedAudio);
    const scoreSound = useSoundEffect(SCORE_SOUND);
    const sadTromboneSound = useSoundEffect(SAD_TROMBONE_SOUND, 0.3);

    const onButtonClick = useCallback(
        (response: ResponseOption) => {
            if (!selectedContact) {
                console.error('No contact selected');
                return;
            }

            const newMessages: Array<TextMessage> = [];
            if (response.type === 'reply') {
                newMessages.push({
                    type: 'sent',
                    message: response.message,
                });
                messageSent.play();
            }

            setConversations({
                ...conversations,
                [selectedContact]:
                    conversations[selectedContact].concat(newMessages),
            });
            setWaitingForResponse(true);

            setTimeout(() => {
                newMessages.push({
                    type: 'received',
                    message:
                        CONVERSATION_STATE_MACHINES[selectedContact][
                            response.nextMessageId
                        ].message,
                });
                messageReceived.play();
                if (
                    !CONVERSATION_STATE_MACHINES[selectedContact][
                        response.nextMessageId
                    ].options
                ) {
                    scoreSound.play();
                }
                setConversations({
                    ...conversations,
                    [selectedContact]:
                        conversations[selectedContact].concat(newMessages),
                });
                setConversationStates({
                    ...conversationStates,
                    [selectedContact]: response.nextMessageId,
                });
                setWaitingForResponse(false);
            }, 800);
        },
        [
            selectedContact,
            conversations,
            messageSent,
            messageReceived,
            conversationStates,
            scoreSound,
        ]
    );

    useEffect(() => {
        const allDone = CONTACTS.every(
            (contact) =>
                !CONVERSATION_STATE_MACHINES[contact][
                    conversationStates[contact]
                ].options
        );
        if (allDone) {
            setTimeout(() => {
                setMinigameState(MinigameState.WON);
            }, 1000);
        }
    }, [conversationStates, markGameDoneCb, goToLandingPage, sadTromboneSound]);

    useEffect(() => {
        if (minigameState === MinigameState.WON) {
            sadTromboneSound.play();
        }
    }, [minigameState, sadTromboneSound]);

    return (
        <TableBackground>
            {minigameState === MinigameState.INTRO && (
                <DialogueBox
                    text={[
                        'Time to see what my friends are up to.',
                        '(Use the suggested responses to talk to each friend.)',
                    ]}
                    afterDialogueCb={(): void =>
                        setMinigameState(MinigameState.PLAYING)
                    }
                />
            )}
            <div className="phone-game">
                <BackButton onClick={goToLandingPage} />
                <div className="phone-wrapper">
                    <img src={PhoneOutline} alt="phone" />
                    <div className="phone-content-wrapper">
                        {selectedContact ? (
                            <Conversation
                                name={selectedContact}
                                messages={conversations[selectedContact]}
                                back={(): void => setSelectedContact(undefined)}
                                suggestedResponses={
                                    !waitingForResponse
                                        ? CONVERSATION_STATE_MACHINES[
                                              selectedContact
                                          ][conversationStates[selectedContact]]
                                              .options
                                        : undefined
                                }
                                sendResponse={onButtonClick}
                            />
                        ) : (
                            <Contacts
                                contacts={Object.entries(conversations).map(
                                    ([name, messages]) => ({
                                        name: name as Contact,
                                        lastMessage:
                                            messages[messages.length - 1]
                                                .message,
                                    })
                                )}
                                selectContact={(name): void =>
                                    setSelectedContact(name)
                                }
                                enabled={
                                    minigameState === MinigameState.PLAYING
                                }
                            />
                        )}
                    </div>
                </div>
            </div>
            {minigameState === MinigameState.WON && (
                <DialogueBox
                    text={[
                        "It's hopeless, the messages never end! I have to just ignore them I guess.",
                    ]}
                    afterDialogueCb={() => {
                        markGameDoneCb();
                        goToLandingPage();
                    }}
                />
            )}
        </TableBackground>
    );
}
